import { useEffect, useState } from "react";
import { AlertTriangle, LogOut } from "lucide-react";
import { clearTokens, setTokens } from "@/lib/api";

// ════════════════════════════════════════════════════════════════════════════
// Impersonation Banner Component
// ════════════════════════════════════════════════════════════════════════════
// Shows a clear visual indicator when a super admin is impersonating a tenant
// user. Provides an easy way to exit impersonation mode and return to the
// original admin session WITHOUT forcing a fresh login.
//
// How the restore works:
//   - AdminTenants.handleImpersonate() stashes the admin's current access +
//     refresh tokens into sessionStorage under "af_admin_session" BEFORE
//     overwriting them with the impersonation token.
//   - When the banner's exit button is clicked, we read that stash and
//     restore the original tokens via setTokens(), clear the impersonation
//     session, and redirect to the returnPath (defaults to /admin/tenants).
//
// If for any reason the stash is missing (e.g. page was reloaded and
// sessionStorage was cleared), we fall back to clearing tokens entirely
// and sending the user to the login page.

interface ImpersonationInfo {
  userId: string;
  email: string;
  name: string | null;
  role: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
}

interface StashedAdminSession {
  accessToken: string;
  refreshToken: string;
  returnPath?: string;
}

export function ImpersonationBanner() {
  const [impersonation, setImpersonation] = useState<ImpersonationInfo | null>(null);

  useEffect(() => {
    // Check for impersonation info in sessionStorage on every mount so the
    // banner stays visible across SPA navigations (fresh mount per route).
    const stored = sessionStorage.getItem("impersonation");
    if (stored) {
      try {
        setImpersonation(JSON.parse(stored));
      } catch {
        sessionStorage.removeItem("impersonation");
      }
    }
  }, []);

  const handleExitImpersonation = () => {
    const stashRaw = sessionStorage.getItem("af_admin_session");
    let returnPath = "/admin/tenants";

    if (stashRaw) {
      try {
        const stash = JSON.parse(stashRaw) as StashedAdminSession;
        if (stash.accessToken) {
          // Restore the admin's original tokens so they're logged back in
          // as themselves without a password prompt.
          setTokens(stash.accessToken, stash.refreshToken || "");
          if (stash.returnPath) returnPath = stash.returnPath;
          sessionStorage.removeItem("af_admin_session");
          sessionStorage.removeItem("impersonation");
          window.location.href = returnPath;
          return;
        }
      } catch {
        // Fall through to the logout path if the stash is malformed.
      }
    }

    // No valid stash — safest fallback is a full logout + login redirect.
    clearTokens();
    sessionStorage.removeItem("af_admin_session");
    sessionStorage.removeItem("impersonation");
    window.location.href = "/login";
  };

  if (!impersonation) {
    return null;
  }

  return (
    <>
      {/*
       * Fixed banner at the top of the viewport. We push the rest of the
       * page content down with a sibling spacer so modals, sidebars, and
       * dashboard headers aren't hidden behind the banner.
       */}
      <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-950 shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <AlertTriangle size={18} className="shrink-0" />
            <span className="text-sm font-medium truncate">
              Impersonating{" "}
              <strong>{impersonation.name || impersonation.email}</strong> at{" "}
              <strong>{impersonation.tenantName}</strong>{" "}
              <code className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-amber-600/30">
                {impersonation.tenantSlug}
              </code>
            </span>
          </div>
          <button
            onClick={handleExitImpersonation}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-700 hover:bg-amber-800 text-white rounded text-sm font-medium transition-colors shrink-0"
          >
            <LogOut size={14} />
            Return to Admin
          </button>
        </div>
      </div>
      {/* Spacer — exactly the banner's height so page content isn't covered */}
      <div className="h-10" aria-hidden="true" />
    </>
  );
}

export default ImpersonationBanner;
